import type { Melody } from '@tunasong/schemas'
import { beatMs, GRID_TOLERANCE } from './grid.js'

/**
 * Playback with feel - pure. Events in, timestamps out; no audio, no React, no clock.
 *
 *   time = onset * beatMs + swingDisplacement + offsetMs
 *
 * The swing displacement moves an off-beat 8th LATER by `swing * beatMs / 3`, so 0 is straight
 * and 1 puts the off-beat at 2/3 of the beat - a full triplet feel. Only off-beat 8ths are
 * displaced: 16ths at .25 and .75 keep their places, which is what "an off-beat 8th" in §4.2 says
 * and what a swung 16th line would need a different rule for.
 *
 * The `quantized` toggle zeroes `offsetMs` AND the swing. Its purpose is to A/B the feel, and
 * swing is feel: a "flat" playback that still pushed every off-beat eighth two thirds of the way
 * through the beat would not be the comparison the toggle exists to offer. Quantized playback puts
 * every note exactly on its grid line.
 *
 * Events come back in note order, which is onset order. They are deliberately NOT re-sorted by
 * `time`: a large negative offset can move a note before its predecessor, and the caller that
 * wants chronological order should say so.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §4.2, §8
 */

export interface ScheduledMelodyEvent {
  /** ms from the start of playback. */
  time: number
  midi: number
  velocity: number
  durationMs: number
}

export interface ScheduleMelodyOptions {
  /** Play the melody flat - both `offsetMs` and the swing treated as 0 - so the feel can be A/B'd. */
  quantized?: boolean
}

/** An off-beat 8th sits half a beat past a beat line. */
const isOffBeatEighth = (onset: number): boolean => {
  const withinBeat = onset - Math.floor(onset)
  return Math.abs(withinBeat - 0.5) <= GRID_TOLERANCE
}

/**
 * How far a note is pushed by the swing, in ms. Exported because the ROLL has to draw the note
 * where the engine will sound it: two copies of `/ 6` would eventually become a picture that
 * disagrees with what the user hears, which is the one thing a feel editor cannot afford.
 */
export const swingDisplacementMs = (onset: number, swing: number, beatMs: number): number =>
  swing > 0 && isOffBeatEighth(onset) ? (swing * beatMs) / 6 : 0

export const scheduleMelody = (melody: Melody, options: ScheduleMelodyOptions = {}): ScheduledMelodyEvent[] => {
  const { quantized = false } = options
  const ms = beatMs(melody.tempo.bpm)
  const swing = quantized ? 0 : (melody.feel?.swing ?? 0)

  return melody.notes.map(note => {
    const displacement = swingDisplacementMs(note.onset, swing, ms)
    return {
      time: note.onset * ms + displacement + (quantized ? 0 : note.offsetMs),
      midi: note.midi,
      velocity: note.velocity,
      durationMs: note.duration * ms,
    }
  })
}

/** How long playback runs - to the end of the last-ending note. */
export const melodyDurationMs = (melody: Melody, options: ScheduleMelodyOptions = {}): number =>
  scheduleMelody(melody, options).reduce((end, event) => Math.max(end, event.time + event.durationMs), 0)
